import React from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import * as TooltipPrimitives from "@radix-ui/react-tooltip";
import { launchableDeployControlClassName } from "components/Launchables/DeployRedesign/shared/configuration/controlStyles";

const instanceNameHelpText =
  "The name of the instance created from this launchable.";

interface InstanceNameConfigurationProps {
  instanceName?: string;
  nameErrorMessage: string;
  onChange?: (name: string) => void;
}

export const InstanceNameConfiguration: React.FC<
  InstanceNameConfigurationProps
> = ({ instanceName, nameErrorMessage, onChange }) => {
  if (!onChange) return null;

  return (
    <div className="px-5 py-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
        <div className="flex h-10 shrink-0 items-center">
          <label
            htmlFor="launchable-instance-name-input"
            className="text-sm font-medium text-zinc-200"
          >
            Instance name
          </label>
          <TooltipPrimitives.Provider>
            <TooltipPrimitives.Root delayDuration={0}>
              <TooltipPrimitives.Trigger asChild>
                <button
                  type="button"
                  className="ml-1 rounded-sm text-zinc-400 outline-none transition-colors hover:text-zinc-200 focus-visible:ring-2 focus-visible:ring-highlight"
                  aria-label="About instance names"
                >
                  <InformationCircleIcon
                    className="h-4 w-4"
                    aria-hidden="true"
                  />
                </button>
              </TooltipPrimitives.Trigger>
              <TooltipPrimitives.Portal>
                <TooltipPrimitives.Content
                  side="top"
                  sideOffset={4}
                  className="z-50 max-w-xs rounded-md bg-zinc-800 px-3 py-2 text-xs text-white shadow-md"
                >
                  {instanceNameHelpText}
                  <TooltipPrimitives.Arrow className="fill-zinc-800" />
                </TooltipPrimitives.Content>
              </TooltipPrimitives.Portal>
            </TooltipPrimitives.Root>
          </TooltipPrimitives.Provider>
        </div>
        <div className="w-full min-w-0 sm:w-96">
          <span id="launchable-instance-name-description" className="sr-only">
            {instanceNameHelpText}
          </span>
          <input
            id="launchable-instance-name-input"
            type="text"
            value={instanceName ?? ""}
            onChange={(event) => onChange(event.target.value)}
            aria-describedby={
              nameErrorMessage
                ? "launchable-instance-name-description launchable-instance-name-error"
                : "launchable-instance-name-description"
            }
            aria-invalid={Boolean(nameErrorMessage)}
            className={`${launchableDeployControlClassName} block w-full px-3 py-2`}
            placeholder="my-launchable"
          />
          {nameErrorMessage && (
            <p
              id="launchable-instance-name-error"
              className="mt-1.5 text-xs text-red-500"
            >
              {nameErrorMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
